<template>
  <b-modal id="add-new-visit-modal"
           size="xl"
           header-class="new-design-modal-header"
           footer-class="new-design-modal-footer"
           title-class="compact-modal-title"
           body-class="scrollable-modal-body"
           content-class="modal-content-full-height"
           @show="onShow"
           @hide="onHide"
           :title="modalTitle"
           centered>
    <template #modal-title>
      <div class="d-flex align-items-center flex-wrap visit-modal-title-row">
        <span class="flex-shrink-0">{{ modalTitle }}</span>
        <div v-if="!isMobile"
             class="flex-grow-1 d-flex justify-content-center">
          <ViewSwitcher v-if="visit && loaded"
                        :value="activeTab"
                        @input="activeTab = $event"
                        :options="tabOptions"/>
        </div>
      </div>
    </template>
    <div v-if="isMobile"
         class="flex-grow-1 d-flex justify-content-center">
      <ViewSwitcher v-if="visit && loaded"
                    :value="activeTab"
                    @input="activeTab = $event"
                    :options="tabOptions"/>
    </div>
    <div class="pt-md-3 pt-2"
         v-if="visit && loaded">
      <div v-show="activeTab === 'description'">
        <VisitModalDescriptionPartNew :visit="visit"
                                      ref="descriptionPart"
                                      :hasControlSystemStatus="hasControlSystemStatus"
                                      @timeChanged="timeChanged = true"
                                      @dateIsInSchedule="dateIsInSchedule"
                                      @changedPatient="patientSet"/>
      </div>
      <div v-show="activeTab === 'additionally'"
           v-if="!editBlock">
        <VisitModalAdditionallyPartNew :visit="visit"
                                       :counter="visitChangeCounter"/>
      </div>
      <div v-show="activeTab === 'notifications'"
           v-if="rights.includes('notifications') && enableNotifications">
        <VisitModalNotificationsPart :visit="visit"/>
      </div>
      <div v-show="activeTab === 'tasks'"
           v-if="rights.includes('tasks') && enableTasks">
        <VisitModalTasksPart :visit="visit"/>
      </div>
      <div v-show="activeTab === 'info'"
           v-if="visit.id">
        <VisitModalInfoPart :visit="visit"/>
      </div>
    </div>
    <template #modal-footer="{ close }">
      <div class="modal-footer-buttons d-flex" style="gap: 12px">
        <b-button class="ml-auto ml-sm-0 d-inline-block d-sm-inline-block align-middle btn-themed btn-themed-outline btn-themed-squared btn-themed-squared-rounder"
                  variant="themed"
                  @click="close()">
          {{ $t('cancel') }}
        </b-button>
        <button :disabled="loading"
                @click="save"
                class="btn-themed align-middle d-inline-block d-sm-inline-block btn-themed-squared btn-themed-squared-rounder">
          {{ $t('save') }}
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script setup>
import {computed, getCurrentInstance, inject, nextTick, onBeforeMount, ref} from "vue"
import {useStore} from "vuex"
import {CalendarService} from "@/services/api/calendar.api.service"
import VisitModalDescriptionPartNew from "@/components/parts/calendar/visitmodal/VisitModalDescriptionPartNew"
import VisitModalAdditionallyPartNew from "@/components/parts/calendar/visitmodal/VisitModalAdditionallyPartNew"
import VisitModalNotificationsPart from "@/components/parts/calendar/visitmodal/VisitModalNotificationsPart"
import VisitModalTasksPart from "@/components/parts/calendar/visitmodal/VisitModalTasksPart"
import VisitModalInfoPart from "@/components/parts/calendar/visitmodal/VisitModalInfoPart"
import ViewSwitcher from "@/components/parts/general/ViewSwitcher"

const store = useStore()
const showReasonPrompt = inject('showReasonPrompt')

let i18n = null
let noty = null
let bvModal = null
onBeforeMount(() => {
  const instance = getCurrentInstance()
  i18n = instance?.ctx?.$i18n
  noty = instance?.appContext?.app?.noty
  bvModal = instance?.ctx?._bv__modal
})

const visit = ref(null)
const loaded = ref(false)
const visitChangeCounter = ref(0)
const inSchedule = ref(true)
const enableNotifications = ref(false)
const enableTasks = ref(false)
const timeChanged = ref(false)
const descriptionPart = ref(null)
const activeTab = ref('description')

const loading = computed(() => store.state.dom.loading)
const newVisitDoctor = computed(() => store.state.calendar.newVisitDoctor)
const newVisitChair = computed(() => store.state.calendar.newVisitChair)
const newVisitId = computed(() => store.state.calendar.newVisitId)
const newVisitPatientId = computed(() => store.state.calendar.newVisitPatientId)
const newVisitDurationMinutes = computed(() => store.state.calendar.newVisitDurationMinutes)
const newVisitLeadId = computed(() => store.state.calendar.newVisitLeadId)
const patientFromCall = computed(() => store.state.calendar.patientFromCall)
const leadForRecord = computed(() => store.state.calendar.leadForRecord)
const clinic = computed(() => store.state.auth.clinic)
const rights = computed(() => store.state.auth.rights)
const branch = computed(() => store.state.auth.branch)
const isMobile = computed(() => store.state.dom.isMobile)
const clientSources = computed(() => store.state.auth.clientSources)
const clinicRecordTypes = computed(() => store.state.auth.record_types)
const editBlock = computed(() => visit.value?.id && visit.value?.record_type === 'block')
const modalTitle = computed(() => {
  if(editBlock.value) return i18n.t('blocked')
  return visit.value && visit.value.id ? (i18n.t('visit')+' #'+visit.value.id) : i18n.t('new_visit')
})
const hasControlSystemStatus = computed(() => !!visit.value?.controlSystemStatus)
const additional = computed(() => clinic.value?.clinic_additional)
const tabOptions = computed(() => {
  if(!visit.value) return []
  const opts = [{id: 'description', title: 'description'}]
  if(!editBlock.value) opts.push({id: 'additionally', title: 'additionally'})
  if(rights.value.includes('notifications') && enableNotifications.value) opts.push({id: 'notifications', title: 'patient_notifications'})
  if(rights.value.includes('tasks') && enableTasks.value) opts.push({id: 'tasks', title: 'related_tasks'})
  if(visit.value.id) opts.push({id: 'info', title: 'info'})
  return opts
})

const save = async () => {
  const needsReason = timeChanged.value && additional.value.ask_reason_reschedule && visit.value.id

  if(needsReason) {
    const reason = await showReasonPrompt()
    if(!reason) return
    visit.value.reason = reason
  }

  if(rights.value.includes('ignore_schedule') && !inSchedule.value) {
    let res = confirm(i18n.t('doctor_is_not_working_on_this_date_continue'))
    if(!res) return
  }
  store.commit('cleanValidationErrors')
  if(visit.value.record_type === 'online' && visit.value.client_id) visit.value.record_type = 'treatment'
  try {
    await CalendarService.createOrUpdateRecord(visit.value)
    noty.info(i18n.t('success_message'))
    bvModal.hide('add-new-visit-modal')
    store.commit('incrementDataTableCounter')
  } catch (e) {
    console.log(e)
  }
}

const initVisit = () => {
  const recordTypeId = clinicRecordTypes.value.find(rt => rt.default_type === 'treatment')?.id || null
  visit.value = {
    doctor_id: null,
    chair_id: null,
    branch_id: branch.value,
    start: null,
    records_statuses_id: 1,
    record_type: 'treatment',
    record_type_id: recordTypeId,
    planned_services: [],
    tasks: [],
    record_notifications: [],
  }
}

const patientSet = (patient_id) => {
  visitChangeCounter.value = patient_id
}
const dateIsInSchedule = (val) => {
  inSchedule.value = val
}

const onShow = async () => {
  if(newVisitId.value) {
    visit.value = await store.dispatch('getClientVisit', {
      id: newVisitPatientId.value,
      visit_id: newVisitId.value
    })
    if(!visit.value.chair_id && newVisitChair.value) {
      visit.value.chair_id = newVisitChair.value.id
    }
    if(!visit.value.client_id && newVisitPatientId.value) {
      visit.value.client_id = newVisitPatientId.value.id
      visit.value.patient = newVisitPatientId.value
    }
  } else {
    initVisit()
    visit.value.doctor_id = newVisitDoctor.value ? newVisitDoctor.value.id : null
    visit.value.chair_id = newVisitChair.value ? newVisitChair.value.id : null
    visit.value.lead_id = newVisitLeadId.value ? newVisitLeadId.value : null
    // note: pre-existing gap carried over from the Options API version - `newVisitTime` was never mapped from the store here, so this always resolved to undefined
    visit.value.start = undefined
    if(newVisitPatientId.value) {
      visit.value.patient = newVisitPatientId.value
      visit.value.client_id = newVisitPatientId.value.id
    } else {
      if(patientFromCall.value) {
        visit.value.patient = patientFromCall.value
        visit.value.client_id = patientFromCall.value.id
      }
    }
    if(leadForRecord.value) {
      visit.value.lead_id = leadForRecord.value.id
      visit.value.source_id = leadForRecord.value.source_id
    }
    if(newVisitDurationMinutes.value) {
      visit.value.durationMinutes = newVisitDurationMinutes.value
    }
  }
  if(leadForRecord.value && !visit.value.source_id) {
    visit.value.source_id = leadForRecord.value.source_id
  }
  await loadPrepare()
  loaded.value = true
  await nextTick()
}

const onHide = () => {
  initVisit()
  loaded.value = false
  visitChangeCounter.value = 0
  timeChanged.value = false
  activeTab.value = 'description'
  store.commit('cleanValidationErrors')
  store.commit('setPatientFromCall', null)
  store.commit('setLeadForRecord', null)
  store.commit('setPhoneFromCall', null)
}

const loadPrepare = async () => {
  if(!clientSources.value.length) {
    await store.dispatch('getClientSources')
  }
}
</script>

<style lang="scss">
// b-modal teleports its DOM elsewhere in the document, completely detached from this component's
// scope-id ancestor, so a scoped :deep() selector can never reach .modal-title here (verified: no
// ancestor of .modal-title carries this component's data-v attribute). This rule is intentionally
// unscoped, and kept safe from leaking to other modals via the :has() marker unique to this one.
.modal-title:has(.visit-modal-title-row) {
  flex: 1 1 auto;
  min-width: 0;
}
</style>

<style scoped lang="scss">
.visit-modal-title-row {
  width: 100%;
}
</style>
